import { useContext, useEffect } from "react";
import { toast } from "@bfc-software/pallet";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  logger,
  AUTHENTICATED,
  AUTH_ERROR,
  AWAITING_ORG_SELECT,
  CREATING_SESSION,
} from "@bfc-software/app-toolbox";
import { validateRequiredString } from "@bfc-software/bfc-web-ui/lib/search-params";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
  DEFAULT_LOCALE,
} from "@bfc-software/bfc-web-ui/i18n";

import OrganizationSelect from "./-components/organization-select";
import AuthContainer from "./-components/auth-container";
import styles from "./redirect.module.css";
import WaitingForRedirect from "./-components/waiting-for-redirect";

type QueryParams = { token: string; stytch_token_type: string };

export const Route = createFileRoute("/$lang/_layout/auth/redirect")({
  component: Redirect,
  validateSearch: (search: QueryParams): QueryParams => {
    return {
      token: validateRequiredString({ val: search.token }),
      stytch_token_type: validateRequiredString({
        val: search.stytch_token_type,
      }),
    };
  },
});

function Redirect() {
  const params = Route.useParams();
  const searchParams = Route.useSearch();
  const { state, startRedirect, selectOrg } = useContext(AuthContext);
  const navigate = useNavigate({ from: "/$lang/auth/redirect" });

  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.redirect,
  });

  // Kick off the redirect flow - always start fresh when landing here with a token
  useEffect(() => {
    logger.info(
      `[Redirect] mount, state.status='${String(state.status)}', hasToken=${!!searchParams.token}`,
    );
    if (searchParams.token) {
      logger.info("[Redirect] calling startRedirect");
      startRedirect(searchParams.token, searchParams.stytch_token_type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on mount/token change, not on state.status
  }, [searchParams.token, searchParams.stytch_token_type, startRedirect]);

  // Navigate to app when authenticated
  useEffect(() => {
    if (state.status !== AUTHENTICATED) return;

    const orgSlug = AuthUtils.getOrgSlug(state, state.orgId);
    logger.info("[Redirect] authenticated, navigating to app", {
      orgId: state.orgId,
      orgSlug,
      locationId: state.locationId,
      orgCount: state.orgs.length,
    });

    navigate({
      // The product picker, matching the password flow. Which product the user
      // wants is their choice, and a warehouse is only chosen once inside one.
      to: `/$lang/orgs/$org`,
      params: {
        lang: DEFAULT_LOCALE,
        org: orgSlug,
      },
    });
  }, [state, navigate]);

  // Show error
  useEffect(() => {
    if (state.status === AUTH_ERROR) {
      toast.error(
        <div className={styles.errorToast}>
          <p className={styles.heading}>{t("errorNotification.message")}</p>
          <p className={styles.errorMessage}>{state.message}</p>
        </div>,
      );
    }
  }, [state, t]);

  let view;
  switch (state.status) {
    case AWAITING_ORG_SELECT: {
      view = (
        <OrganizationSelect
          flow="login"
          orgId=""
          goBack={{ label: "", shouldRender: false, handler: () => {} }}
          email={state.email}
          setParentForm={({ orgId }) => {
            if (orgId) selectOrg(orgId);
          }}
          orgs={state.orgs}
          lang={params.lang}
        />
      );
      break;
    }
    case AUTH_ERROR: {
      view = null;
      break;
    }
    default: {
      const email = "email" in state ? state.email : "";
      const locationName =
        (state.status === CREATING_SESSION || state.status === AUTHENTICATED) &&
        state.locationId
          ? (AuthUtils.getLocationName(state, state.orgId, state.locationId) ??
            "")
          : "";
      view = <WaitingForRedirect email={email} locationName={locationName} />;
    }
  }

  return <AuthContainer>{view}</AuthContainer>;
}
